import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

export const openApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// ===============================
// Interceptors globais (API)
// ===============================

// Evita várias tentativas simultâneas de refresh
let isRefreshing = false;
let failedQueue = [];

const makeFakeResponse = (error) => {
  const status = error?.response?.status || 0;
  return {
    data: null,
    status,
    statusText: "",
    headers: error?.response?.headers || {},
    config: error?.config || {},
    request: error?.request,
  };
};

const processQueue = (token = null) => {
  failedQueue.forEach((p) => p.resolve(token));
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem("token");
  delete api.defaults.headers.common.Authorization;

  // evita loop infinito
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// 1) Sempre tentar colocar Authorization (se existir no localStorage)
api.interceptors.request.use(
  (config) => {
    const tokenStr = localStorage.getItem("token");

    if (tokenStr) {
      try {
        const token = JSON.parse(tokenStr);
        if (token) config.headers.Authorization = `Bearer ${token}`;
      } catch (_) {
        localStorage.removeItem("token");
        delete config.headers.Authorization;
      }
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 2) Tratar 401/403 globalmente (inclui requests feitos antes do useAuth)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config || {};
    const status = error?.response?.status;
    const url = originalRequest?.url || "";

    // Se nem tem response (rede/timeout), não mexe
    if (!status) return Promise.reject(error);

    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/refresh_token") ||
      url.includes("/auth/logout");

    // Se o próprio refresh deu 401/403 -> desloga sem estourar overlay
    if ((status === 401 || status === 403) && url.includes("/auth/refresh_token")) {
      processQueue(null);
      clearAuthAndRedirect();
      return Promise.resolve(makeFakeResponse(error));
    }

    // Token expirado/sem sessão -> tenta refresh 1x (para qualquer rota protegida)
    if ((status === 401 || status === 403) && !isAuthRoute) {
      if (originalRequest._retry) {
        processQueue(null);
        clearAuthAndRedirect();
        return Promise.resolve(makeFakeResponse(error));
      }

      originalRequest._retry = true;

      // Se já tem refresh rodando, espera
      if (isRefreshing) {
        return new Promise((resolve) => {
          failedQueue.push({ resolve });
        }).then((token) => {
          if (!token) return Promise.resolve(makeFakeResponse(error));
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const { data } = await api.post("/auth/refresh_token");
        const newToken = data?.token;

        if (!newToken) {
          processQueue(null);
          clearAuthAndRedirect();
          return Promise.resolve(makeFakeResponse(error));
        }

        localStorage.setItem("token", JSON.stringify(newToken));
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        processQueue(newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(null);
        clearAuthAndRedirect();
        return Promise.resolve(makeFakeResponse(error));
      } finally {
        isRefreshing = false;
      }
    }

    // Para 401/403 nas rotas de auth, não deixa overlay “morrer” a UI
    if ((status === 401 || status === 403) && isAuthRoute) {
      return Promise.resolve(makeFakeResponse(error));
    }
    // ✅ 404 de mídia/public (arquivo inexistente) -> não derruba a UI
    if (status === 404 && url.includes("/public/")) {
      return Promise.resolve(makeFakeResponse(error));
    }


    return Promise.reject(error);
  }
);

export default api;
